/**
 * useIdleDetector — disconnects SignalR after user inactivity.
 *
 * Monitors mousemove, keydown, scroll, and touchstart events on the document.
 * When no activity is detected for `timeoutMs` milliseconds, calls `onIdle`.
 * When the user interacts after being idle, calls `onResume`.
 *
 * In kiosk mode (`kioskMode = true`), the hook is a no-op: no listeners are
 * registered and the callbacks are never fired.
 *
 * Design doc: §4 "Refresh Policy: Idle Timeout and Kiosk Mode"
 */

import { useEffect, useRef, useCallback } from 'react';

export interface UseIdleDetectorOptions {
  /** Idle timeout in milliseconds. Default: 15 * 60 * 1000 (15 minutes). */
  timeoutMs: number;
  /** When true, the hook is a no-op — SignalR stays connected indefinitely. */
  kioskMode: boolean;
  /** Called when the user goes idle (timeout expired). */
  onIdle: () => void;
  /** Called when the user interacts after being idle. */
  onResume: () => void;
}

export function useIdleDetector({
  timeoutMs,
  kioskMode,
  onIdle,
  onResume,
}: UseIdleDetectorOptions): { isIdle: boolean } {
  const isIdleRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stable refs for callbacks to avoid re-registering listeners on every render
  const onIdleRef = useRef(onIdle);
  const onResumeRef = useRef(onResume);
  onIdleRef.current = onIdle;
  onResumeRef.current = onResume;

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!isIdleRef.current) {
        isIdleRef.current = true;
        onIdleRef.current();
      }
    }, timeoutMs);
  }, [timeoutMs]);

  useEffect(() => {
    // Kiosk mode: no idle detection, no listeners, no callbacks.
    if (kioskMode) return;

    // If timeout is 0 or negative, treat as no-timeout (same as kiosk).
    if (timeoutMs <= 0) return;

    const onActivity = () => {
      if (isIdleRef.current) {
        isIdleRef.current = false;
        onResumeRef.current();
      }
      resetTimer();
    };

    // Activity events
    const events: Array<keyof DocumentEventMap> = [
      'mousemove', 'keydown', 'scroll', 'touchstart',
    ];

    for (const evt of events) {
      document.addEventListener(evt, onActivity, { passive: true });
    }

    // Start the initial timer
    resetTimer();

    return () => {
      for (const evt of events) {
        document.removeEventListener(evt, onActivity);
      }
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [kioskMode, timeoutMs, resetTimer]);

  return { isIdle: isIdleRef.current };
}
